/**
 * Unit Translation Map
 * 
 * Translates common English cooking measurement units to supported languages.
 * Used as a CLIENT-SIDE fallback when the backend translation doesn't cover units.
 */

const UNIT_TRANSLATIONS = {
  // Volume - spoons
  tbsp:        { es: 'cda',          it: 'cucchiai',    fr: 'c. à s.',    de: 'EL' },
  tablespoon:  { es: 'cucharada',    it: 'cucchiaio',   fr: 'cuillère à soupe', de: 'Esslöffel' },
  tablespoons: { es: 'cucharadas',   it: 'cucchiai',    fr: 'cuillères à soupe', de: 'Esslöffel' },
  tsp:         { es: 'cdta',         it: 'cucchiaino',  fr: 'c. à c.',    de: 'TL' },
  teaspoon:    { es: 'cucharadita',  it: 'cucchiaino',  fr: 'cuillère à café', de: 'Teelöffel' },
  teaspoons:   { es: 'cucharaditas', it: 'cucchiaini',  fr: 'cuillères à café', de: 'Teelöffel' },

  // Volume - cups/glasses
  cup:         { es: 'taza',         it: 'tazza',       fr: 'tasse',       de: 'Tasse' },
  cups:        { es: 'tazas',        it: 'tazze',       fr: 'tasses',      de: 'Tassen' },
  glass:       { es: 'vaso',         it: 'bicchiere',   fr: 'verre',       de: 'Glas' },
  shot:        { es: 'chupito',      it: 'bicchierino', fr: 'mesure',      de: 'Schuss' },

  // Volume - liquid
  liter:       { es: 'litro',        it: 'litro',       fr: 'litre',       de: 'Liter' },
  liters:      { es: 'litros',       it: 'litri',       fr: 'litres',      de: 'Liter' },

  // Quantities
  pinch:       { es: 'pizca',        it: 'pizzico',     fr: 'pincée',      de: 'Prise' },
  dash:        { es: 'chorrito',     it: 'goccio',      fr: 'trait',       de: 'Spritzer' },
  piece:       { es: 'unidad',       it: 'pezzo',       fr: 'pièce',       de: 'Stück' },
  pieces:      { es: 'unidades',     it: 'pezzi',       fr: 'pièces',      de: 'Stück' },
  unit:        { es: 'unidad',       it: 'unità',       fr: 'unité',       de: 'Stück' },
  packet:      { es: 'paquete',      it: 'bustina',     fr: 'sachet',      de: 'Päckchen' },
  pcs:         { es: 'uds',          it: 'pz',          fr: 'pcs',         de: 'Stk' },

  // Size descriptors
  small:       { es: 'pequeño',      it: 'piccolo',     fr: 'petit',       de: 'klein' },
  medium:      { es: 'mediano',      it: 'medio',       fr: 'moyen',       de: 'mittel' },
  large:       { es: 'grande',       it: 'grande',      fr: 'grand',       de: 'groß' },
  whole:       { es: 'entero',       it: 'intero',      fr: 'entier',      de: 'ganz' },

  // Herbs/produce
  clove:       { es: 'diente',       it: 'spicchio',    fr: 'gousse',      de: 'Zehe' },
  cloves:      { es: 'dientes',      it: 'spicchi',     fr: 'gousses',     de: 'Zehen' },
  sprig:       { es: 'ramita',       it: 'rametto',     fr: 'brin',        de: 'Zweig' },
  sprigs:      { es: 'ramitas',      it: 'rametti',     fr: 'brins',       de: 'Zweige' },
  bunch:       { es: 'manojo',       it: 'mazzo',       fr: 'botte',       de: 'Bund' },
  bunches:     { es: 'manojos',      it: 'mazzi',       fr: 'bottes',      de: 'Bünde' },
  stalk:       { es: 'tallo',        it: 'gambo',       fr: 'tige',        de: 'Stängel' },
  stalks:      { es: 'tallos',       it: 'gambi',       fr: 'tiges',       de: 'Stängel' },
  leaf:        { es: 'hoja',         it: 'foglia',      fr: 'feuille',     de: 'Blatt' },
  leaves:      { es: 'hojas',        it: 'foglie',      fr: 'feuilles',    de: 'Blätter' },
  head:        { es: 'cabeza',       it: 'testa',       fr: 'tête',        de: 'Kopf' },
  bulb:        { es: 'bulbo',        it: 'bulbo',       fr: 'bulbe',       de: 'Knolle' },

  // Cuts/shapes
  slice:       { es: 'rodaja',       it: 'fetta',       fr: 'tranche',     de: 'Scheibe' },
  slices:      { es: 'rodajas',      it: 'fette',       fr: 'tranches',    de: 'Scheiben' },
  sheets:      { es: 'láminas',      it: 'fogli',       fr: 'feuilles',    de: 'Blätter' },
  stick:       { es: 'rama',         it: 'stecca',      fr: 'bâton',       de: 'Stange' },
  handful:     { es: 'puñado',       it: 'manciata',    fr: 'poignée',     de: 'Handvoll' },

  // Descriptive
  'to taste':     { es: 'al gusto',     it: 'q.b.',        fr: 'au goût',     de: 'nach Geschmack' },
  'for frying':   { es: 'para freír',   it: 'per friggere', fr: 'pour friture', de: 'zum Braten' },
  'as needed':    { es: 'al gusto',     it: 'q.b.',        fr: 'au besoin',   de: 'nach Bedarf' },
  quantity:       { es: 'cantidad',     it: 'quantità',    fr: 'quantité',    de: 'Menge' },
};

/**
 * Translate a unit string to the target language.
 * Returns the original unit if no translation is found (metric units like g, kg, ml, l stay the same).
 * 
 * @param {string} unit - The unit to translate
 * @param {string} lang - Target language code (es, it, fr, de)
 * @returns {string} Translated unit or original if not found/not needed
 */
export const translateUnit = (unit, lang) => {
  if (!unit || lang === 'en') return unit;

  const key = unit.toLowerCase().trim();
  const translation = UNIT_TRANSLATIONS[key];

  if (translation && translation[lang]) {
    return translation[lang];
  }

  return unit;
};

/**
 * Translate an amount string when it contains text-based descriptors.
 * Handles cases where "to taste", "for frying", etc. are stored as amounts.
 * 
 * @param {string|number} amount - The amount value
 * @param {string} lang - Target language code
 * @returns {string|number} Translated amount or original if numeric/not found
 */
export const translateAmount = (amount, lang) => {
  if (!amount || lang === 'en') return amount;
  if (typeof amount === 'number') return amount;

  const str = String(amount).trim();
  // Only translate if the amount is purely text (not a number)
  if (/^\d/.test(str)) return amount;

  const key = str.toLowerCase();
  const translation = UNIT_TRANSLATIONS[key];
  if (translation && translation[lang]) {
    return translation[lang];
  }

  return amount;
};

// ============================================
// COMMON INGREDIENT NAME TRANSLATIONS
// ============================================
const INGREDIENT_TRANSLATIONS = {
  // Proteins
  beef:           { es: 'carne de res',   it: 'manzo',            fr: 'boeuf',           de: 'Rindfleisch' },
  chicken:        { es: 'pollo',          it: 'pollo',            fr: 'poulet',          de: 'Hähnchen' },
  pork:           { es: 'cerdo',          it: 'maiale',           fr: 'porc',            de: 'Schweinefleisch' },
  lamb:           { es: 'cordero',        it: 'agnello',          fr: 'agneau',          de: 'Lamm' },
  fish:           { es: 'pescado',        it: 'pesce',            fr: 'poisson',         de: 'Fisch' },
  shrimp:         { es: 'camarones',      it: 'gamberi',          fr: 'crevettes',       de: 'Garnelen' },
  egg:            { es: 'huevo',          it: 'uovo',             fr: 'oeuf',            de: 'Ei' },
  eggs:           { es: 'huevos',         it: 'uova',             fr: 'oeufs',           de: 'Eier' },

  // Vegetables
  onion:          { es: 'cebolla',        it: 'cipolla',          fr: 'oignon',          de: 'Zwiebel' },
  onions:         { es: 'cebollas',       it: 'cipolle',          fr: 'oignons',         de: 'Zwiebeln' },
  garlic:         { es: 'ajo',            it: 'aglio',            fr: 'ail',             de: 'Knoblauch' },
  potatoes:       { es: 'patatas',        it: 'patate',           fr: 'pommes de terre', de: 'Kartoffeln' },
  potato:         { es: 'patata',         it: 'patata',           fr: 'pomme de terre',  de: 'Kartoffel' },
  carrots:        { es: 'zanahorias',     it: 'carote',           fr: 'carottes',        de: 'Karotten' },
  carrot:         { es: 'zanahoria',      it: 'carota',           fr: 'carotte',         de: 'Karotte' },
  tomato:         { es: 'tomate',         it: 'pomodoro',         fr: 'tomate',          de: 'Tomate' },
  tomatoes:       { es: 'tomates',        it: 'pomodori',         fr: 'tomates',         de: 'Tomaten' },
  'bell pepper':  { es: 'pimiento',       it: 'peperone',         fr: 'poivron',         de: 'Paprika' },
  celery:         { es: 'apio',           it: 'sedano',           fr: 'céleri',          de: 'Sellerie' },
  mushrooms:      { es: 'champiñones',    it: 'funghi',           fr: 'champignons',     de: 'Pilze' },
  spinach:        { es: 'espinacas',      it: 'spinaci',          fr: 'épinards',        de: 'Spinat' },
  peas:           { es: 'guisantes',      it: 'piselli',          fr: 'petits pois',     de: 'Erbsen' },
  corn:           { es: 'maíz',           it: 'mais',             fr: 'maïs',            de: 'Mais' },
  cabbage:        { es: 'repollo',        it: 'cavolo',           fr: 'chou',            de: 'Kohl' },
  lettuce:        { es: 'lechuga',        it: 'lattuga',          fr: 'laitue',          de: 'Salat' },
  beans:          { es: 'frijoles',       it: 'fagioli',          fr: 'haricots',        de: 'Bohnen' },
  lentils:        { es: 'lentejas',       it: 'lenticchie',       fr: 'lentilles',       de: 'Linsen' },

  // Pantry
  salt:           { es: 'sal',            it: 'sale',             fr: 'sel',             de: 'Salz' },
  'black pepper': { es: 'pimienta negra', it: 'pepe nero',        fr: 'poivre noir',     de: 'schwarzer Pfeffer' },
  pepper:         { es: 'pimienta',       it: 'pepe',             fr: 'poivre',          de: 'Pfeffer' },
  sugar:          { es: 'azúcar',         it: 'zucchero',         fr: 'sucre',           de: 'Zucker' },
  flour:          { es: 'harina',         it: 'farina',           fr: 'farine',          de: 'Mehl' },
  rice:           { es: 'arroz',          it: 'riso',             fr: 'riz',             de: 'Reis' },
  pasta:          { es: 'pasta',          it: 'pasta',            fr: 'pâtes',           de: 'Nudeln' },
  bread:          { es: 'pan',            it: 'pane',             fr: 'pain',            de: 'Brot' },
  'olive oil':    { es: 'aceite de oliva', it: 'olio d\'oliva',   fr: 'huile d\'olive',  de: 'Olivenöl' },
  oil:            { es: 'aceite',         it: 'olio',             fr: 'huile',           de: 'Öl' },
  butter:         { es: 'mantequilla',    it: 'burro',            fr: 'beurre',          de: 'Butter' },
  cream:          { es: 'crema',          it: 'panna',            fr: 'crème',           de: 'Sahne' },
  milk:           { es: 'leche',          it: 'latte',            fr: 'lait',            de: 'Milch' },
  cheese:         { es: 'queso',          it: 'formaggio',        fr: 'fromage',         de: 'Käse' },
  water:          { es: 'agua',           it: 'acqua',            fr: 'eau',             de: 'Wasser' },
  vinegar:        { es: 'vinagre',        it: 'aceto',            fr: 'vinaigre',        de: 'Essig' },
  'vegetable broth': { es: 'caldo de verduras', it: 'brodo vegetale', fr: 'bouillon de légumes', de: 'Gemüsebrühe' },
  'chicken broth': { es: 'caldo de pollo', it: 'brodo di pollo',  fr: 'bouillon de poulet', de: 'Hühnerbrühe' },

  // Herbs & Spices
  basil:          { es: 'albahaca',       it: 'basilico',         fr: 'basilic',         de: 'Basilikum' },
  parsley:        { es: 'perejil',        it: 'prezzemolo',       fr: 'persil',          de: 'Petersilie' },
  oregano:        { es: 'orégano',        it: 'origano',          fr: 'origan',          de: 'Oregano' },
  thyme:          { es: 'tomillo',        it: 'timo',             fr: 'thym',            de: 'Thymian' },
  rosemary:       { es: 'romero',         it: 'rosmarino',        fr: 'romarin',         de: 'Rosmarin' },
  cinnamon:       { es: 'canela',         it: 'cannella',         fr: 'cannelle',        de: 'Zimt' },
  cumin:          { es: 'comino',         it: 'cumino',           fr: 'cumin',           de: 'Kreuzkümmel' },
  paprika:        { es: 'pimentón',       it: 'paprica',          fr: 'paprika',         de: 'Paprikapulver' },
  'bay leaf':     { es: 'hoja de laurel', it: 'alloro',           fr: 'feuille de laurier', de: 'Lorbeerblatt' },
  'bay leaves':   { es: 'hojas de laurel', it: 'foglie di alloro', fr: 'feuilles de laurier', de: 'Lorbeerblätter' },
  ginger:         { es: 'jengibre',       it: 'zenzero',          fr: 'gingembre',       de: 'Ingwer' },
  saffron:        { es: 'azafrán',        it: 'zafferano',        fr: 'safran',          de: 'Safran' },
  lemon:          { es: 'limón',          it: 'limone',           fr: 'citron',          de: 'Zitrone' },
  'lemon juice':  { es: 'jugo de limón',  it: 'succo di limone',  fr: 'jus de citron',   de: 'Zitronensaft' },
};

// ============================================
// COOKING NOTE / PREP TRANSLATIONS
// ============================================
const NOTE_TRANSLATIONS = {
  chopped:                  { es: 'picado/a',         it: 'tritato/a',         fr: 'haché(e)',          de: 'gehackt' },
  minced:                   { es: 'picado/a fino',    it: 'tritato/a finemente', fr: 'émincé(e)',       de: 'fein gehackt' },
  diced:                    { es: 'en cubos',         it: 'a cubetti',         fr: 'en dés',            de: 'gewürfelt' },
  sliced:                   { es: 'en rodajas',       it: 'a fette',           fr: 'en tranches',       de: 'in Scheiben' },
  grated:                   { es: 'rallado/a',        it: 'grattugiato/a',     fr: 'râpé(e)',           de: 'gerieben' },
  peeled:                   { es: 'pelado/a',         it: 'pelato/a',          fr: 'pelé(e)',           de: 'geschält' },
  crushed:                  { es: 'machacado/a',      it: 'schiacciato/a',     fr: 'écrasé(e)',         de: 'zerdrückt' },
  toasted:                  { es: 'tostado/a',        it: 'tostato/a',         fr: 'grillé(e)',         de: 'geröstet' },
  'cut into small pieces':  { es: 'cortado en trozos pequeños', it: 'tagliato a pezzetti', fr: 'coupé en petits morceaux', de: 'in kleine Stücke geschnitten' },
  'cut into chunks':        { es: 'cortado en trozos', it: 'tagliato a pezzi', fr: 'coupé en morceaux', de: 'in Stücke geschnitten' },
  'peeled and chopped':     { es: 'pelado/a y picado/a', it: 'pelato/a e tritato/a', fr: 'pelé(e) et haché(e)', de: 'geschält und gehackt' },
  'peeled and sliced':      { es: 'pelado/a y en rodajas', it: 'pelato/a e affettato/a', fr: 'pelé(e) et tranché(e)', de: 'geschält und in Scheiben' },
  'peeled and diced':       { es: 'pelado/a y en cubos', it: 'pelato/a e a cubetti', fr: 'pelé(e) et en dés', de: 'geschält und gewürfelt' },
  'peeled and quartered':   { es: 'pelado/a y en cuartos', it: 'pelato/a e in quarti', fr: 'pelé(e) et en quartiers', de: 'geschält und geviertelt' },
  'finely chopped':         { es: 'finamente picado/a', it: 'tritato/a finemente', fr: 'finement haché(e)', de: 'fein gehackt' },
  'for seasoning':          { es: 'para sazonar',     it: 'per condire',       fr: 'pour assaisonner',  de: 'zum Würzen' },
  'for garnish':            { es: 'para decorar',     it: 'per guarnire',      fr: 'pour garnir',       de: 'zum Garnieren' },
  'chopped for garnish':    { es: 'picado para decorar', it: 'tritato per guarnire', fr: 'haché pour garnir', de: 'gehackt zum Garnieren' },
  'for sautéing':           { es: 'para saltear',     it: 'per soffriggere',   fr: 'pour faire revenir', de: 'zum Anbraten' },
  optional:                 { es: 'opcional',          it: 'facoltativo',       fr: 'facultatif',        de: 'optional' },
  dry:                      { es: 'seco/a',            it: 'secco/a',           fr: 'sec/sèche',         de: 'trocken' },
  fresh:                    { es: 'fresco/a',          it: 'fresco/a',          fr: 'frais/fraîche',     de: 'frisch' },
  frozen:                   { es: 'congelado/a',       it: 'surgelato/a',       fr: 'surgelé(e)',        de: 'tiefgefroren' },
};

/**
 * Translate a common ingredient name to the target language.
 * Only translates well-known ingredient names. Returns original for unknown items.
 */
export const translateIngredientName = (name, lang) => {
  if (!name || lang === 'en') return name;

  const key = name.toLowerCase().trim();
  const translation = INGREDIENT_TRANSLATIONS[key];
  if (translation && translation[lang]) {
    return translation[lang];
  }

  return name;
};

/**
 * Translate cooking notes/prep instructions.
 */
export const translateNote = (note, lang) => {
  if (!note || lang === 'en') return note;

  const key = note.toLowerCase().trim();
  const translation = NOTE_TRANSLATIONS[key];
  if (translation && translation[lang]) {
    return translation[lang];
  }

  return note;
};

export default UNIT_TRANSLATIONS;
